import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { faker } from '@faker-js/faker';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Customer, CustomerCreateDto, CustomerSearchDto } from '../../models/customer.interface';
import { ApiResponse } from '@app/core/adaptors/dto';
import {
  parametersFromPath,
  parametersFromRequest,
} from '@app/core/interceptors/mock-api.interceptor';

@Injectable({ providedIn: 'root' })
export class CustomerMocksService {
  private readonly STORAGE_KEY = 'mock_customers';
  private customers: Customer[] = [];

  constructor() {
    this.loadCustomersFromStorage();
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const { tenantId, resourceId } = parametersFromPath(req, 'customers');
    if (req.method === 'GET') {
      if (resourceId) {
        return this.getCustomerById(resourceId);
      } else {
        const queryParams = parametersFromRequest(req);
        const searchDto = { ...queryParams };
        return this.getCustomers(tenantId, searchDto);
      }
    } else if (req.method === 'POST') {
      const saveDto = req.body as CustomerCreateDto;
      return this.saveCustomer(tenantId, saveDto);
    } else if (req.method === 'PUT' && resourceId) {
      const updatedCustomer = req.body as Customer;
      return this.updateCustomer(updatedCustomer);
    } else {
      throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  saveCustomer(tenantId: number, customer: CustomerCreateDto): Observable<HttpEvent<any>> {
    const newCustomer: Customer = {
      id: this.customers.length + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tenantId,
      status: 'Active',
      ...customer,
      // Mirrors the API: Contact Person is only captured for TRADE customers,
      // so a NORMAL customer's own name is copied across.
      contactName: customer.customerType === 'TRADE' ? (customer.contactName ?? '') : customer.name,
    };
    this.customers.push(newCustomer);
    this.saveCustomersToStorage();
    return this.mapToResponse(newCustomer, 201);
  }

  updateCustomer(updatedCustomer: Customer) {
    const existingCustomerIndex = this.customers.findIndex((c) => c.id === updatedCustomer.id);
    if (existingCustomerIndex !== -1) {
      this.customers[existingCustomerIndex] = updatedCustomer;
    }
    this.saveCustomersToStorage();
    return this.mapToResponse(updatedCustomer, 200);
  }

  getCustomers(tenantId: number, searchCriteria: CustomerSearchDto): Observable<HttpEvent<any>> {
    const { limit, page, query, sortField, sortOrder } = searchCriteria;
    if (this.customers.length === 0) {
      this.generateMockCustomers();
      this.saveCustomersToStorage();
    }

    const filteredData = this.customers
      .filter((c) => c.tenantId === tenantId && anyQueryMatch(c, query))
      .sort((a, b) => {
        const aValue = a[sortField as keyof Customer];
        const bValue = b[sortField as keyof Customer];
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return sortOrder === 'asc'
          ? (aValue as any) - (bValue as any)
          : (bValue as any) - (aValue as any);
      });

    const data = filteredData.slice((page - 1) * limit, page * limit);

    const res: ApiResponse<Customer> = {
      data,
      total: filteredData.length,
      limit,
      page,
      totalPages: Math.ceil(filteredData.length / limit),
    };
    console.log('Mock response:', res.limit, res.page, res.total, res.totalPages);
    return this.mapToResponse(res, 200);
  }

  getCustomerById(id: number): Observable<HttpEvent<any>> {
    const customer = this.customers.find((c) => c.id === id);
    if (customer) {
      return this.mapToResponse(customer, 200);
    } else {
      return this.mapToResponse({ message: 'Customer not found' }, 404);
    }
  }

  // Method for other services to get customers synchronously
  getMockCustomers(): Customer[] {
    if (this.customers.length === 0) {
      this.generateMockCustomers();
      this.saveCustomersToStorage();
    }
    return [...this.customers]; // Return a copy to prevent external modification
  }

  private loadCustomersFromStorage(): void {
    try {
      const storedCustomers = localStorage.getItem(this.STORAGE_KEY);
      if (storedCustomers) {
        this.customers = JSON.parse(storedCustomers);
        console.log(`Loaded ${this.customers.length} customers from localStorage`);
      }
    } catch (error) {
      console.error('Error loading customers from localStorage:', error);
      this.customers = [];
    }
  }

  private saveCustomersToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.customers));
      console.log(`Saved ${this.customers.length} customers to localStorage`);
    } catch (error) {
      console.error('Error saving customers to localStorage:', error);
    }
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(
      delay(500) // Simulate network delay
    );
  }

  private generateMockCustomers() {
    // Generate mock customers
    for (let i = 1; i <= 25; i++) {
      this.customers.push(this.newCustomer(i));
    }
  }

  private newCustomer(customerId: number): Customer {
    return {
      id: customerId,
      tenantId: 1,
      name: faker.company.name(),
      contactName: faker.person.fullName(),
      email: faker.internet.email(),
      mobilePhone: this.generateNZPhoneNumber(),
      homePhone: '',
      workPhone: this.generateNZPhoneNumber(),
      address: this.generateNZAddress(),
      createdAt: faker.date.past().toISOString(),
      updatedAt: faker.date.past().toISOString(),
      status: 'Active',
      customerType: 'NORMAL',
      smsConsent: false,
    };
  }

  // Helper method to generate NZ phone numbers

  private generateNZPhoneNumber(): string {
    const prefixes = ['021', '022', '025', '027', '028', '029'];
    const prefix = faker.helpers.arrayElement(prefixes);
    const number = faker.string.numeric(7);
    return `+64 ${prefix} ${number.substring(0, 3)} ${number.substring(3)}`;
  }

  // Helper method to generate NZ addresses

  private generateNZAddress(): string {
    const nzCities = [
      'Auckland',
      'Wellington',
      'Christchurch',
      'Hamilton',
      'Tauranga',
      'Napier',
      'Palmerston North',
      'Dunedin',
      'Rotorua',
      'Whanganui',
    ];

    const streetTypes = ['Street', 'Road', 'Avenue', 'Drive', 'Lane', 'Place'];

    const streetNumber = faker.number.int({ min: 1, max: 999 });
    const streetName = faker.person.lastName();
    const streetType = faker.helpers.arrayElement(streetTypes);
    const city = faker.helpers.arrayElement(nzCities);

    return `${streetNumber} ${streetName} ${streetType}, ${city}`;
  }
}

const anyQueryMatch = (customer: Customer, query: string): boolean => {
  if (!query?.trim()) {
    return true;
  }

  const search = query.toLowerCase().trim();
  const isNameMatch = customer.name?.toLowerCase().includes(search);
  const isContactNameMatch = customer.contactName?.toLowerCase().includes(search);
  const isMobilePhoneMatch = customer.mobilePhone
    ?.replace(/\s+/g, '')
    .toLowerCase()
    .includes(search);
  const isWorkPhoneMatch = customer.workPhone?.replace(/\s+/g, '').toLowerCase().includes(search);
  const isHomePhoneMatch = customer.homePhone?.replace(/\s+/g, '').toLowerCase().includes(search);

  return (
    isNameMatch || isContactNameMatch || isMobilePhoneMatch || isWorkPhoneMatch || isHomePhoneMatch
  );
};
